import * as z from "zod";
import { ReaderIdSchema } from "@bindery/shared/auth/readerId";
import { Provenance } from "@bindery/speech/contract";

const Instant = z.int().nonnegative();
const Probability = z.number().min(0).max(1);
const TokenId = z.uuid();

type Range = readonly [first: number, last: number];

// `\p{L}` is above the browser floor's regex syntax (Firefox 74), so a
// letter is a character with case or one inside a caseless script's letter
// blocks, the scripts' own punctuation carved out.
const CASELESS_LETTERS: readonly Range[] = [
  [0x0591, 0x05bd],
  [0x05bf, 0x05bf],
  [0x05c1, 0x05c2],
  [0x05c4, 0x05c5],
  [0x05c7, 0x05f2],
  [0x0620, 0x0669],
  [0x066e, 0x06d3],
  [0x06d5, 0x06ff],
  [0x0900, 0x0963],
  [0x0966, 0x096f],
  [0x0971, 0x0dff],
  [0x0e01, 0x0e3a],
  [0x0e40, 0x0e4e],
  [0x1100, 0x11ff],
  [0x3041, 0x309f],
  [0x30a1, 0x30fa],
  [0x30fc, 0x30ff],
  [0x3131, 0x318e],
  [0x3400, 0x4dbf],
  [0x4e00, 0x9fff],
  [0xac00, 0xd7a3],
  [0xf900, 0xfaff],
  [0x20000, 0x3134f],
];

const isCaselessLetter = (ch: string): boolean => {
  const point = ch.codePointAt(0) ?? 0;
  return CASELESS_LETTERS.some(
    ([first, last]) => point >= first && point <= last,
  );
};
const isLetter = (ch: string): boolean =>
  ch.toLowerCase() !== ch.toUpperCase() || isCaselessLetter(ch);
const isDigit = (ch: string): boolean => ch >= "0" && ch <= "9";
// Combining diacritics, so a decomposed "café" stays one word.
const isCombiningMark = (ch: string): boolean =>
  ch >= "\u0300" && ch <= "\u036f";
export const isWordChar = (ch: string): boolean =>
  isLetter(ch) || isDigit(ch) || isCombiningMark(ch);

/** Code points, not UTF-16 units, so a letter outside the BMP stays whole. */
export const codePoints = (text: string): string[] => Array.from(text);

export const isCapitalized = (text: string): boolean => {
  const first = text.charAt(0);
  return first !== first.toLowerCase();
};
const isJoiner = (ch: string): boolean =>
  ch === "'" || ch === "’" || ch === "-";

/** Letters and digits, optionally joined by apostrophes or hyphens inside. */
export const isWordText = (text: string): boolean => {
  const chars = codePoints(text);
  const first = chars[0];
  const last = chars[chars.length - 1];
  if (first === undefined || last === undefined) return false;
  if (!isLetter(first) && !isDigit(first)) return false;
  if (!isWordChar(last) && last !== "'" && last !== "’") return false;
  return chars.every((ch) => isWordChar(ch) || isJoiner(ch));
};

export const WordText = z.string().refine(isWordText, "not a word");
export type WordText = z.infer<typeof WordText>;

export const SightStatus = z.enum([
  "sight",
  "emerging",
  "predicted-sight",
  "predicted-unknown",
  "unobserved",
]);
export type SightStatus = z.infer<typeof SightStatus>;

export const SightWord = z.object({
  word: z.string(),
  status: SightStatus,
  level: z.int().min(0).max(5).nullable(),
  pKnown: Probability.nullable(),
  modelRelease: z.string().min(1).nullable(),
  evaluatedAt: Instant,
});
export type SightWord = z.infer<typeof SightWord>;

export const JudgementSource = z.enum([
  "decisions-api",
  "description-lm",
  "child",
  "adult",
]);
export type JudgementSource = z.infer<typeof JudgementSource>;

// `model` answered for intendedConfidence and the probabilities (the scorer);
// `generator` proposed the alternatives, absent when they are deterministic.
// Two fields, not one joined id, so a query can ask which snapshot scored.
export const IntentJudgement = z.object({
  source: JudgementSource,
  model: z.string().min(1).nullable(),
  generator: z.string().min(1).nullable().optional(),
  intendedConfidence: Probability.nullable(),
  alternatives: z
    .array(z.object({ word: WordText, probability: Probability.nullable() }))
    .max(4),
  at: Instant,
});
export type IntentJudgement = z.infer<typeof IntentJudgement>;

export const WordOrigin = z.enum([
  "dictated",
  "re-said",
  "chosen",
  "typed",
  "adult",
]);
export type WordOrigin = z.infer<typeof WordOrigin>;

export const TokenResolution = z.enum(["unreviewed", "surfaced", "confirmed"]);
export type TokenResolution = z.infer<typeof TokenResolution>;

export const AsrSpan = z
  .object({
    dictationId: z.uuid(),
    startMs: z.number().nonnegative(),
    endMs: z.number().nonnegative(),
    segmentLogprob: z.number().nonpositive().nullable(),
  })
  .refine((s) => s.startMs <= s.endMs, "ends before it starts");
export type AsrSpan = z.infer<typeof AsrSpan>;

export const WordToken = z.object({
  kind: z.literal("word"),
  id: TokenId,
  text: WordText,
  origin: WordOrigin,
  asr: AsrSpan.nullable(),
  intent: z.array(IntentJudgement),
  resolution: TokenResolution,
  properNoun: z.boolean(),
  // Ids of the tokens a re-say replaced, transitively, so a word's correction
  // history survives the new ids a replacement mints.
  supersedes: z.array(TokenId),
});
export type WordToken = z.infer<typeof WordToken>;

export const PunctToken = z.object({
  kind: z.literal("punct"),
  id: TokenId,
  text: z.string().min(1).max(3),
});
export type PunctToken = z.infer<typeof PunctToken>;

// Never carries text, not even the ASR's guess (commitment 2).
export const UnresolvedToken = z.object({
  kind: z.literal("unresolved"),
  id: TokenId,
  cause: z.enum(["saved-for-adult", "flagged"]),
  asr: AsrSpan.nullable(),
  supersedes: z.array(TokenId),
});
export type UnresolvedToken = z.infer<typeof UnresolvedToken>;

export const CompositionToken = z.discriminatedUnion("kind", [
  WordToken,
  PunctToken,
  UnresolvedToken,
]);
export type CompositionToken = z.infer<typeof CompositionToken>;

export const LadderRung = z.enum([
  "hear-and-compare",
  "choose",
  "type",
  "re-say",
  "tell-me-about-it",
  "save-for-adult",
]);
export type LadderRung = z.infer<typeof LadderRung>;

const TokenIds = z.array(TokenId).min(1);

export const ReSayResult = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("replaced"),
    tokens: z.array(CompositionToken).min(1),
    asr: Provenance,
  }),
  z.object({ outcome: z.literal("same-word") }),
  z.object({ outcome: z.literal("no-speech") }),
  z.object({ outcome: z.literal("unaligned") }),
]);
export type ReSayResult = z.infer<typeof ReSayResult>;

export const DescribeResult = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("chosen"),
    to: WordText,
    // The scorer, null when scoring did not answer; the generator proposed.
    model: z.string().min(1).nullable(),
    generator: z.string().min(1).optional(),
    candidates: z.array(WordText).min(1).max(3),
  }),
  z.object({ outcome: z.literal("none-of-these") }),
  z.object({ outcome: z.literal("no-candidates") }),
]);
export type DescribeResult = z.infer<typeof DescribeResult>;

export const CompositionEdit = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("insert-dictation"),
    atIndex: z.int().min(0),
    tokens: z.array(CompositionToken).min(1),
    asr: Provenance,
    at: Instant,
  }),
  z.object({
    op: z.literal("insert-typed"),
    atIndex: z.int().min(0),
    tokenId: TokenId,
    text: WordText,
    at: Instant,
  }),
  z.object({
    op: z.literal("judge"),
    judgements: z
      .array(z.object({ tokenId: TokenId, judgement: IntentJudgement }))
      .min(1),
    at: Instant,
  }),
  z.object({ op: z.literal("surface"), tokenIds: TokenIds, at: Instant }),
  z.object({ op: z.literal("confirm"), tokenId: TokenId, at: Instant }),
  z.object({
    op: z.literal("reject"),
    tokenId: TokenId,
    rung: z.enum(["hear-and-compare", "choose", "type"]),
    at: Instant,
  }),
  z.object({
    op: z.literal("choose"),
    tokenId: TokenId,
    from: WordText,
    to: WordText,
    at: Instant,
  }),
  z.object({
    op: z.literal("type"),
    tokenId: TokenId,
    from: WordText,
    to: WordText,
    at: Instant,
  }),
  z.object({
    op: z.literal("re-say"),
    tokenIds: TokenIds,
    result: ReSayResult,
    at: Instant,
  }),
  z.object({
    op: z.literal("describe"),
    tokenId: TokenId,
    failedTranscriptions: z.array(z.string().min(1)).min(1),
    result: DescribeResult,
    at: Instant,
  }),
  z.object({ op: z.literal("save-for-adult"), tokenId: TokenId, at: Instant }),
  z.object({
    op: z.literal("adult-resolve"),
    tokenId: TokenId,
    text: WordText,
    at: Instant,
  }),
  z.object({
    op: z.literal("build"),
    tokenId: TokenId,
    target: WordText,
    wrongTaps: z.int().min(0),
    at: Instant,
  }),
  z.object({ op: z.literal("delete"), tokenIds: TokenIds, at: Instant }),
  z.object({
    op: z.literal("move"),
    tokenIds: TokenIds,
    toIndex: z.int().min(0),
    at: Instant,
  }),
]);
export type CompositionEdit = z.infer<typeof CompositionEdit>;
export type EditOp = CompositionEdit["op"];

// An audience exists only once someone decides it, like JUDGE_SEES_PROMPT.
export const ShareAudience = z.enum(["teacher", "parent"]);
export type ShareAudience = z.infer<typeof ShareAudience>;

export const ShareState = z.discriminatedUnion("state", [
  z.object({ state: z.literal("private") }),
  z.object({
    state: z.literal("pending-review"),
    audience: z.array(ShareAudience).min(1),
    requestedAt: Instant,
  }),
  z.object({
    state: z.literal("shared"),
    audience: z.array(ShareAudience).min(1),
    // The reviewing adult's broker subject, never the child's.
    approvedBy: z.string().min(1),
    consentRef: z.string().min(1),
    sharedAt: Instant,
  }),
  z.object({ state: z.literal("withdrawn"), at: Instant }),
]);
export type ShareState = z.infer<typeof ShareState>;

export const CompositionPhase = z.enum(["creating", "editing", "finished"]);
export type CompositionPhase = z.infer<typeof CompositionPhase>;

export const Composition = z
  .object({
    id: z.uuid(),
    readerId: ReaderIdSchema,
    title: z.string().max(80).nullable(),
    tokens: z.array(CompositionToken),
    edits: z.array(CompositionEdit),
    phase: CompositionPhase,
    share: ShareState,
    createdAt: Instant,
    finishedAt: Instant.nullable(),
  })
  .refine(
    (c) => (c.phase === "finished") === (c.finishedAt !== null),
    "finishedAt is set exactly when the phase is finished",
  );
export type Composition = z.infer<typeof Composition>;
